import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { TYPE_ICONS } from './InsightsMenu';
import { OBSERVED_STRIP, PIN, PIN_GAP, PIN_HIT as HIT } from './prepare';
import {
  explainPointer, observedCard, observedLabel, pointerSummary, pointerTitle, pointerWhen,
} from '../../constants/insightsText';
import { ResizeHandle, useResizableWidth } from './useResizableWidth';

// PIN: the visible disc; HIT: its button, and the spacing between pins.
const CARD_W = 320;
// The side card: HOW_W wide unless resized (by its outer edge), never under HOW_MIN.
const HOW_W = 360;
const HOW_MIN = 280;
// An Observed block's button: at least this wide, and taller than the strip
// (a mark's place on the time axis is its meaning, so it is not spread out).
const OBS_HIT_W = 12;
const OBS_HIT_H = 14;
const OBS_W = 300;
const obsKey = (o) => `${o.type}:${o.start}`;
const MINUS = '−';
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

// Spread overlapping pins sideways, keeping each cluster centred on its times,
// then keep them inside [lo, hi] without squeezing a cluster at an edge: push
// right from lo, then left from hi, HIT apart.
export function spreadPins(items, lo, hi) {
  const out = items.slice().sort((a, b) => a.x - b.x).map((it) => ({ ...it, px: it.x }));
  for (let k = 0; k < out.length;) {
    let j = k;
    while (j + 1 < out.length && out[j + 1].x - out[j].px < HIT) {
      out[j + 1].px = out[j].px + HIT;
      j += 1;
    }
    const shift = (out.slice(k, j + 1).reduce((s, it) => s + it.x - it.px, 0)) / (j - k + 1);
    for (let m = k; m <= j; m += 1) out[m].px += shift;
    k = j + 1;
  }
  out.forEach((it, i) => { it.px = Math.max(it.px, i ? out[i - 1].px + HIT : lo); });
  for (let i = out.length - 1; i >= 0; i -= 1) {
    out[i].px = Math.min(out[i].px, i < out.length - 1 ? out[i + 1].px - HIT : hi);
  }
  return out;
}

// Strong pins are filled; in dark mode the glyph goes dark, since white on the
// light brand-400 / accent-400 fills is under 3:1. Weak pins are rings.
const pinClass = (p) => {
  if (p.strength !== 'strong') {
    return 'bg-surface dark:bg-gray-870 border border-gray-400 dark:border-gray-500 text-gray-500 dark:text-gray-400';
  }
  return p.type === 'ramp_up'
    ? 'bg-accent-600 dark:bg-accent-400 text-white dark:text-gray-900'
    : 'bg-brand-600 dark:bg-brand-400 text-white dark:text-gray-900';
};

const popoverBox = 'overflow-auto rounded-lg bg-white dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 shadow-lg p-3 text-sm pointer-events-auto';
const popoverClass = `absolute z-30 ${popoverBox}`;

// `observed`: the measured ramps on the Observed strip, each with `covered`
// (a shown warning of its direction overlaps it), or null when the strip is
// not drawn; `onAbout(tab)` opens the About panel on a tab.
export default function InsightsOverlay({
  getPlot, tick, pointers, params, medianAt, onHighlight, observed = null, onAbout,
}) {
  const rootRef = useRef(null);
  const titleId = useId();
  const obsTitleId = useId();
  const [geom, setGeom] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [pinnedId, setPinnedId] = useState(null);
  const [howOpen, setHowOpen] = useState(false);
  const [obsHover, setObsHover] = useState(null);
  const [obsPinned, setObsPinned] = useState(null);
  const pinRefs = useRef({});
  const blockRefs = useRef({});
  const refocusing = useRef(false);
  const howRoom = useRef(() => HOW_W);
  const howSize = useResizableWidth({
    storageKey: 'insightsHowWidth', min: HOW_MIN, initial: HOW_W, max: () => howRoom.current(),
  });

  useLayoutEffect(() => {
    const plot = getPlot();
    if (!plot || !rootRef.current) {
      setGeom(null);
      return;
    }
    const o = plot.over.getBoundingClientRect();
    const r = rootRef.current.getBoundingClientRect();
    setGeom({
      left: o.left - r.left, top: o.top - r.top, width: o.width, height: o.height,
      rootWidth: r.width || o.left - r.left + o.width + 16, rootHeight: r.height,
    });
  }, [getPlot, tick, pointers, observed]);

  const activeId = pinnedId || hoverId;
  const active = pointers.find((p) => p.id === activeId) || null;
  const activeObsKey = obsPinned || obsHover;
  const activeObs = (observed || []).find((o) => obsKey(o) === activeObsKey) || null;

  useEffect(() => {
    const mark = active || activeObs;
    onHighlight(mark ? { start: mark.start, end: mark.end, kind: mark.type } : null);
  }, [active, activeObs, onHighlight]);

  // Closing a pinned card hands focus back to its pin, without the focus
  // reopening it as a hover card.
  const closeCard = useCallback(() => {
    const pin = pinRefs.current[pinnedId];
    setHowOpen(false);
    setPinnedId(null);
    setHoverId(null);
    refocusing.current = true;
    pin?.focus();
    refocusing.current = false;
  }, [pinnedId]);

  // The same for a pinned Observed card and its block.
  const closeObs = useCallback(() => {
    const block = blockRefs.current[obsPinned];
    setObsPinned(null);
    setObsHover(null);
    refocusing.current = true;
    block?.focus();
    refocusing.current = false;
  }, [obsPinned]);

  useEffect(() => {
    // Capture phase, so the innermost layer closes first (side card, then the
    // pinned card, then a hover card) and the Insights menu or About panel
    // only see Escape once nothing here is open.
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (howOpen) setHowOpen(false);
      else if (pinnedId) closeCard();
      else if (obsPinned) closeObs();
      else if (hoverId) setHoverId(null);
      else if (obsHover) setObsHover(null);
      else return;
      e.stopPropagation();
    };
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setHowOpen(false);
        setPinnedId(null);
        setObsPinned(null);
      }
    };
    window.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown);
    };
  }, [howOpen, pinnedId, hoverId, obsPinned, obsHover, closeCard, closeObs]);

  const plot = getPlot();
  const pins = geom && plot
    ? spreadPins(
      pointers
        .map((p) => ({ p, x: geom.left + plot.valToPos(Date.parse(p.pin) / 1000, 'x') }))
        .filter((it) => it.x >= geom.left && it.x <= geom.left + geom.width),
      geom.left + HIT / 2,
      geom.left + geom.width - HIT / 2,
    )
    : [];
  const anchor = active && pins.find((it) => it.p.id === active.id);
  const pinned = Boolean(pinnedId) && active && active.id === pinnedId;

  // Narrow (phone) charts shrink the cards to fit. The side card goes right of
  // the card where HOW_W fits, else left of it, else on the side with more
  // room; with no room beside the card it covers it until its own x closes it.
  // Its side never depends on its width, so resizing does not make it jump; it
  // widens from its outer edge, as far as that side's room.
  const rootWidth = geom?.rootWidth ?? 0;
  const cardW = Math.min(CARD_W, rootWidth - 16);
  const cardLeft = anchor ? clamp(anchor.px - cardW / 2, 8, rootWidth - cardW - 8) : 0;
  const roomRight = rootWidth - 4 - (cardLeft + cardW + 10);
  const roomLeft = cardLeft - 10 - 4;
  let howSide = 'over';
  if (roomRight >= HOW_W) howSide = 'right';
  else if (roomLeft >= HOW_W) howSide = 'left';
  else if (Math.max(roomRight, roomLeft) >= HOW_MIN) howSide = roomRight >= roomLeft ? 'right' : 'left';
  howRoom.current = () => ({ right: roomRight, left: roomLeft }[howSide] ?? rootWidth - 8);
  const howW = Math.min(howSize.width, howRoom.current());
  let howLeft = clamp(cardLeft, 4, rootWidth - howW - 4);
  if (howSide === 'right') howLeft = cardLeft + cardW + 10;
  else if (howSide === 'left') howLeft = cardLeft - 10 - howW;
  const how = pinned && howOpen ? explainPointer(active, params) : null;
  // Cards scroll inside the chart instead of running past its bottom.
  const maxHeight = geom?.rootHeight ? Math.max(120, geom.rootHeight - geom.top - 12) : undefined;

  // Observed blocks' buttons: clipped to the plot as drawn, then at least OBS_HIT_W wide.
  const stripTop = geom ? geom.top + geom.height + OBSERVED_STRIP.y : 0;
  const blocks = geom && plot && observed
    ? observed.map((o) => {
      const a = Math.max(0, plot.valToPos(Date.parse(o.start) / 1000, 'x'));
      const b = Math.min(geom.width, plot.valToPos(Date.parse(o.end) / 1000, 'x'));
      if (b <= 0 || a >= geom.width) return null;
      const w = Math.max(b - a, OBS_HIT_W);
      return { o, x: geom.left + (a + b) / 2, left: geom.left + clamp(a - (w - (b - a)) / 2, 0, geom.width - w), w };
    }).filter(Boolean)
    : [];
  const obsAnchor = activeObs && blocks.find((it) => it.o === activeObs);
  const obsW = Math.min(OBS_W, rootWidth - 16);
  const obs = obsAnchor ? observedCard(activeObs, params?.observed_rule || { steps: 4 }, activeObs.covered) : null;

  const zoom = () => {
    const a = Date.parse(active.start) / 1000;
    const b = Date.parse(active.end) / 1000;
    const pad = Math.max(3600, (b - a) * 0.35);
    setHowOpen(false);
    plot.setScale('x', { min: a - pad, max: b + pad });
  };

  return (
    <div ref={rootRef} className="absolute inset-0 pointer-events-none animate-fadeIn">
      {pins.map(({ p, x, px }) => {
        const Icon = TYPE_ICONS[p.type];
        return (
          <React.Fragment key={p.id}>
            {Math.abs(px - x) > 1 && (
              <span aria-hidden="true" className="absolute w-px bg-gray-300 dark:bg-gray-600"
                style={{ left: x, top: geom.top - 6, height: 6 }} />
            )}
            <button
              ref={(el) => { pinRefs.current[p.id] = el; }}
              type="button"
              aria-label={`${pointerTitle(p)}, ${pointerWhen(p)}`}
              aria-expanded={pinnedId === p.id}
              className="absolute pointer-events-auto rounded-full flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              style={{ left: px - HIT / 2, top: geom.top - PIN_GAP - HIT, width: HIT, height: HIT }}
              onMouseEnter={() => setHoverId(p.id)}
              onMouseLeave={() => setHoverId(null)}
              onFocus={() => { if (!refocusing.current) setHoverId(p.id); }}
              onBlur={() => setHoverId(null)}
              onClick={() => { setPinnedId(p.id); setHowOpen(false); setObsPinned(null); }}
            >
              <span aria-hidden="true" className={`flex items-center justify-center rounded-full ${pinClass(p)}`} style={{ width: PIN, height: PIN }}>
                <Icon className="h-3 w-3" />
              </span>
            </button>
          </React.Fragment>
        );
      })}

      {/* Each block's card follows it, so Tab goes from a pinned block to its link. */}
      {blocks.map(({ o, left, w }) => (
        <React.Fragment key={obsKey(o)}>
          <button
            ref={(el) => { blockRefs.current[obsKey(o)] = el; }}
            type="button"
            aria-label={observedLabel(o)}
            aria-expanded={obsPinned === obsKey(o)}
            className="absolute pointer-events-auto rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ring-offset-2 ring-offset-surface dark:ring-offset-gray-870"
            style={{ left, top: stripTop + OBSERVED_STRIP.h / 2 - OBS_HIT_H / 2, width: w, height: OBS_HIT_H }}
            onMouseEnter={() => setObsHover(obsKey(o))}
            onMouseLeave={() => setObsHover(null)}
            onFocus={() => { if (!refocusing.current) setObsHover(obsKey(o)); }}
            onBlur={() => setObsHover(null)}
            onClick={() => { setObsPinned(obsKey(o)); setPinnedId(null); setHowOpen(false); }}
          />
          {obs && activeObs === o && (
            <div role="dialog" aria-labelledby={obsTitleId} className={popoverClass}
              style={{
                left: clamp(obsAnchor.x - obsW / 2, 8, rootWidth - obsW - 8),
                bottom: geom.rootHeight - stripTop + 6,
                width: obsW,
                maxHeight: Math.max(80, stripTop - 12),
              }}>
              <p id={obsTitleId} className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">{obs.title}</p>
              <p className="mt-1 text-[12.5px] leading-relaxed text-gray-700 dark:text-gray-300">{obs.what}</p>
              <p className="mt-1 text-[12.5px] font-medium text-gray-900 dark:text-gray-100">{obs.verdict}</p>
              {obsPinned && (
                <button
                  type="button"
                  className="mt-2 w-full border-t border-gray-200/70 dark:border-gray-600/70 pt-2 text-left text-[12.5px] font-medium text-brand-600 dark:text-brand-300 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  onClick={() => { setObsPinned(null); onAbout?.('ramp'); }}
                >
                  What counts as a ramp
                </button>
              )}
            </div>
          )}
        </React.Fragment>
      ))}

      {active && anchor && (
        <div role="dialog" aria-labelledby={titleId} className={popoverClass} style={{ left: cardLeft, top: geom.top + 4, width: cardW, maxHeight }}>
          <h3 id={titleId} className="text-sm font-semibold text-gray-900 dark:text-gray-100">{pointerTitle(active)}</h3>
          <p className="mt-0.5 font-mono text-xs text-gray-500 dark:text-gray-300">{pointerWhen(active)}</p>
          <p className="mt-2 text-[13px] leading-relaxed text-gray-700 dark:text-gray-300">
            {pointerSummary(active, { median: medianAt(Date.parse(active.metrics.at || active.pin) / 1000), params })}
          </p>
          {pinned && (
            <>
              <button
                type="button"
                aria-expanded={howOpen}
                className="mt-2 w-full border-t border-gray-200/70 dark:border-gray-600/70 pt-2 text-left text-[12.5px] font-medium text-brand-600 dark:text-brand-300 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                onClick={() => setHowOpen((v) => !v)}
              >
                How is this calculated?
              </button>
              <div className="mt-2 flex gap-2">
                <button type="button" onClick={zoom}
                  className="rounded-md border border-brand-300 bg-brand-50 dark:bg-brand-900/30 dark:border-brand-700 px-2.5 py-1 text-xs font-medium text-brand-700 dark:text-brand-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                  Zoom to this period
                </button>
                <button type="button" onClick={closeCard}
                  className="rounded-md border border-gray-200/70 dark:border-gray-600/70 px-2.5 py-1 text-xs text-gray-500 dark:text-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {how && (
        <div data-testid="how-card" className="absolute z-30 pointer-events-auto" style={{ left: howLeft, top: geom.top + 4, width: howW }}>
          <ResizeHandle resize={howSize} edge={howSide === 'right' ? 'right' : 'left'} label="Resize the explanation" />
          <aside aria-label="How is this calculated?" className={popoverBox} style={{ maxHeight }}>
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">How is this calculated?</p>
                <p className="text-xs text-gray-500 dark:text-gray-300">{pointerTitle(active)}</p>
              </div>
              <button type="button" aria-label="Close explanation" onClick={() => setHowOpen(false)}
                className="rounded-md border border-gray-200/70 dark:border-gray-600/70 p-1 text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <ol className="mt-2 list-decimal pl-5 space-y-1 text-[12.5px] leading-relaxed text-gray-700 dark:text-gray-300">
              {how.steps.map((s) => <li key={s}>{s}</li>)}
            </ol>
            <pre className="mt-2 whitespace-pre-wrap rounded-md bg-gray-50 dark:bg-gray-800 px-2.5 py-2 font-mono text-[11.5px] text-gray-900 dark:text-gray-100">
              {how.formula.join('\n').replace(/ - /g, ` ${MINUS} `)}
            </pre>
            {how.measured && (
              <p className="mt-2 border-t border-gray-200/70 dark:border-gray-600/70 pt-2 text-[12.5px] font-medium text-gray-900 dark:text-gray-100">
                {how.measured}
              </p>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
